import { useQuery } from '@tanstack/react-query'

import { queryClient } from '@sparkai/utils'

import { apiClient } from '../../apiClient'
import { MSPerformanceSummaryKey } from '../programs/usePerformanceSummary'
import { MSUnifiedOverviewKey } from '../programs/useUnifiedOverview'
import {
  MSProgramInstanceTasksReviewMineInfiniteKey,
  MSProgramInstanceTasksReviewMineKey,
  MSTasksReviewMineInfiniteKey,
  MSTasksReviewMineKey,
} from './useGetMine'

export type TaskReviewAnnotation = {
  id: string
  parentId?: string | null
  featureId?: string
  data: {
    tool: string
    answer?: string
    correctAnswer?: string
    class?: string
    mediaId?: string
    vertices?: { x: number; y: number }[]
    question?: string
  }
}

export type TaskReviewResponse = {
  taskId: string
  timeToClose: number
  dateClosed: string
  engagementDateCreated: string
  isCorrect: boolean | null
  isLocked: boolean
  engagementId: string
  request: {
    annotations: TaskReviewAnnotation[]
  }
  resolution: {
    response?: string
    hasEscalated?: boolean
    escalationReason?: string | null
    annotations: TaskReviewAnnotation[]
  }
  groundTruth: null | {
    hasEscalated?: boolean
    escalationReason?: string | null
    annotations?: TaskReviewAnnotation[]
  }
  media: {
    mediaId: string
    mimeType?: string
    location: string
  }[]
  program: {
    programId: string
    displayName: string
    operatorDisplayName: string
    iconType: string
    color: string
  }
  programInstance?: {
    programInstanceId: string
    name: string | null
    displayName: string | null
    programGroup: {
      programGroupId: string
      name: string
      displayName: string | null
    }
  } | null
}

export const MSTasksReviewByIdKey = 'mission-specialist-tasks-review-by-id'

type QueryRetry = boolean | number | ((failureCount: number, error: unknown) => boolean)

export function useGetById(taskReviewId?: string, options?: { retry?: QueryRetry }) {
  return useQuery<TaskReviewResponse, Error>({
    queryKey: [MSTasksReviewByIdKey, taskReviewId],
    queryFn: async () => {
      const { data } = await apiClient.get(`/int/v3/operators/task-review/${taskReviewId}`)

      // Opening a task review marks it as read server-side. Invalidate list/summary queries
      // so unread and incorrect counters refresh immediately in the mission specialist UI.
      void queryClient.invalidateQueries({
        queryKey: [MSTasksReviewMineKey],
      })
      void queryClient.invalidateQueries({
        queryKey: [MSTasksReviewMineInfiniteKey],
      })
      void queryClient.invalidateQueries({
        queryKey: [MSProgramInstanceTasksReviewMineKey],
      })
      void queryClient.invalidateQueries({
        queryKey: [MSProgramInstanceTasksReviewMineInfiniteKey],
      })
      void queryClient.invalidateQueries({
        queryKey: [MSPerformanceSummaryKey],
      })
      void queryClient.invalidateQueries({
        queryKey: [MSUnifiedOverviewKey],
      })

      return data
    },
    enabled: Boolean(taskReviewId),
    refetchOnWindowFocus: false,
    retry: options?.retry,
  })
}
